import React, { useMemo, forwardRef } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { BASE_OPTIONS, EJE_X_BASE, EJE_Y_BASE, lineaAnotacion } from './chartBase';
import { COLOR, FUENTE, UMBRALES } from './tokens';
import InfoGrafico from './InfoGrafico';
import { MOCK_AUSENTISMO_CANAL, MOCK_PROMEDIO_AUSENTISMO } from './mockDataEjemplos';

const ESCALA_MIN = 25; // % — piso de la escala cuando todos los canales están bajos.

/**
 * Ausentismo por canal de origen real — línea de referencia en el promedio del
 * estudio (el MISMO número del KPI de arriba, nunca hardcodeado). Filtra canales con
 * n < 20 (dato ciego).
 * @param {{data: Array<{nombreCanal: string, total: number, noAsistio: number, tasaAusentismo: number}>, promedioEstudio: number}} props
 */
const AusentismoPorCanal = forwardRef(({ data, promedioEstudio, compacto = false }, ref) => {
    // La muestra y el "(n=…)" son los turnos RESUELTOS (vinieron o faltaron): es el denominador real del %.
    // Antes mostraban el total del canal (con cancelados y abiertos) y el % salía de otra cantidad.
    const filas = useMemo(
        () => [...(data || [])].filter((f) => (f.resueltos ?? f.total) >= UMBRALES.muestraMinima).sort((a, b) => Number(b.tasaAusentismo) - Number(a.tasaAusentismo)),
        [data]
    );
    const sinClasificar = (data || []).find((f) => f.nombreCanal === 'Sin clasificar');

    // Escala proporcional al máximo real de los datos, nunca un tope fijo: si un canal
    // tiene 288% de ausentismo (dato con problemas de calidad ajenos a esta pantalla),
    // la barra lo refleja en vez de cortarse igual que una de 25%.
    const escalaMax = useMemo(() => {
        const maxReal = filas.length ? Math.max(...filas.map((f) => Number(f.tasaAusentismo))) : 0;
        return Math.max(ESCALA_MIN, Math.ceil((maxReal * 1.15) / 5) * 5);
    }, [filas]);

    const colorFila = (valor) => {
        if (promedioEstudio == null) return COLOR.azul; // sin promedio no hay contra qué comparar
        if (valor > promedioEstudio + 3) return COLOR.rojo;
        if (valor > promedioEstudio) return COLOR.ambar;
        return COLOR.verde;
    };

    const chartData = {
        labels: filas.map((f) => `${f.nombreCanal} (n=${f.resueltos ?? f.total})`),
        datasets: [{
            data: filas.map((f) => Number(f.tasaAusentismo)),
            backgroundColor: filas.map((f) => colorFila(Number(f.tasaAusentismo))),
            borderRadius: 4,
            barThickness: 18,
        }],
    };

    const options = {
        ...BASE_OPTIONS,
        indexAxis: 'y',
        plugins: {
            ...BASE_OPTIONS.plugins,
            legend: { display: false },
            annotation: {
                annotations: promedioEstudio == null ? {} : {
                    promedio: lineaAnotacion(promedioEstudio, COLOR.negro, `promedio ${promedioEstudio.toFixed(1)}%`, { eje: 'x', dash: [2, 2] }),
                },
            },
            tooltip: {
                ...BASE_OPTIONS.plugins.tooltip,
                callbacks: { label: (ctx) => `${ctx.raw.toFixed(1)}%` },
            },
        },
        scales: {
            x: { ...EJE_Y_BASE, max: escalaMax },
            y: { ...EJE_X_BASE, grid: { display: false } },
        },
    };

    return (
        <Card variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo="Ausentismo por canal"
                        queMuestra="Te dice qué canal de origen trae clientes que faltan más seguido a su turno, comparando cada uno contra el promedio general del estudio en vez de un número fijo."
                        ejemploVisual={<AusentismoPorCanal compacto data={MOCK_AUSENTISMO_CANAL} promedioEstudio={MOCK_PROMEDIO_AUSENTISMO} />}
                        comoLeerlo={[
                            'Cada barra es un canal con muestra suficiente (al menos 20 turnos resueltos en el período — con menos, el dato es poco confiable y no se muestra).',
                            '"(n=X)" al lado del nombre del canal es la cantidad de turnos resueltos (vinieron o faltaron, sin cancelados ni sobreturnos): el denominador del %, para poder juzgar qué tan confiable es.',
                            'La línea negra punteada es el ausentismo del estudio en el período: el complemento exacto del KPI "Asistencia" del encabezado (100 − asistencia), calculado sobre la misma base que cada canal.',
                            `Verde = ausentismo ≤ ${MOCK_PROMEDIO_AUSENTISMO}% (el promedio). Ámbar = por encima del promedio pero hasta 3 puntos más. Rojo = más de 3 puntos por encima del promedio.`,
                        ]}
                        analisis="Instagram está en rojo con 23.3% de ausentismo, más de 8 puntos por encima del promedio del estudio (15%) — con 30 turnos de muestra, es una diferencia real, no ruido estadístico. Facebook Bot, en cambio, está en verde (10%): la gente que agenda por bot parece comprometerse más con el turno que la que agenda por Instagram, quizás porque el proceso del bot ya filtra mejor la intención real de venir."
                    />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>Ausentismo por canal</Typography>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '14px' }}>
                        Canal de origen real (marketing), cargado vía catálogo de referencias.
                    </Typography>
                </>
            )}
            {filas.length === 0 ? (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisTexto }}>
                    No hay canales con suficiente muestra (al menos {UMBRALES.muestraMinima} turnos resueltos) en el período.
                </Typography>
            ) : (
                <Box sx={{ height: compacto ? Math.max(100, filas.length * 32) : Math.max(140, filas.length * 40) }}>
                    <Bar ref={ref} data={chartData} options={options} />
                </Box>
            )}
            {!compacto && sinClasificar && (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 11.5, color: COLOR.grisTexto, marginTop: '14px' }}>
                    {sinClasificar.total} turno(s) quedaron "Sin clasificar" — conviene volver obligatorio el campo de canal
                    desde el catálogo de referencias.
                </Typography>
            )}
        </Card>
    );
});

export default AusentismoPorCanal;
